/**
 * Wire protocol constants shared by the realtime server and the client.
 *
 * Both sides MUST read every width and bound from this file. A mismatch here is
 * silent corruption, not a crash: the reader consumes the wrong number of bits
 * and everything after it decodes to garbage. Never change a width without
 * changing both sides in the same commit.
 */

/** Message type ids. Numeric, not strings: Colyseus encodes a numeric type in a
 * single byte, whereas a string type costs 1 + length on every packet (verified
 * in S1-001). Ids are permanent — append, never renumber. */
export const MSG = {
  /** S1-001 scaffold: server echoes the payload back unmodified. */
  ECHO: 0,
  /** C→S fixed-size input packet. */
  INPUT: 1,
  /** S→C delta snapshot. */
  SNAPSHOT: 2,
  /** S→C once on join: which entity id belongs to this client. */
  WELCOME: 3,
  /**
   * S→C broadcast, one per resolved shot: who fired, whether it connected.
   *
   * Event-driven rather than per-tick, and deliberately tiny (3 bytes): the tracer's
   * direction is not sent because the shooter's yaw and pitch are already in the
   * snapshot, so every client can draw the line itself. Adding an impact point would
   * have cost 44 more bits per shot for something the client can compute.
   */
  SHOT: 5,
  /**
   * S→C match state: phase, clock, team scores, scoreboard rows.
   *
   * msgpack rather than a hand-rolled codec, like SERVER_STATS and for the same reasons:
   * it carries no per-tick simulation state, it goes out about once a second (and on every
   * phase change), and it is read by UI code rather than by the simulation. A bit-packed
   * scoreboard would save perhaps 40 bytes a second and cost a codec to maintain.
   */
  MATCH_STATE: 6,
  /**
   * S→C loop diagnostics, once a second.
   *
   * The only message that is NOT hand-encoded binary: it carries no gameplay state,
   * runs at 1Hz, and is read by the debug overlay rather than the simulation. Paying
   * msgpack's overhead here buys a readable payload and no codec to maintain; the rule
   * that gameplay bytes are ours is unaffected.
   */
  SERVER_STATS: 4,
  /**
   * S→C, to one client only: the authoritative state of *your* weapon.
   *
   * The one deliberately per-recipient message in the protocol, and the exception is
   * earned. Magazines, reload deadlines and switch timers are predicted client-side from
   * the player's own inputs, so they are correct until an input is dropped — and inputs do
   * get dropped, under queue overflow or a starved tick. Broadcasting every player's
   * magazine to everyone would cost 8x the bytes to tell seven clients something none of
   * them can see; sending it only to its owner costs 5 bytes and only when it changes.
   */
  WEAPON: 7,
  /**
   * S→C broadcast: a grenade left someone's hand.
   *
   * Carries the *cause* — origin, direction, tick — and not the effect. The arc is
   * deterministic and every client already shares the map, so each one computes the identical
   * trajectory. That makes a grenade cost 12 bytes for its whole life instead of a position
   * every snapshot for two seconds, and lets the thrower see it leave their hand immediately.
   */
  PROJECTILE_THROW: 8,
  /**
   * S→C broadcast: a grenade went off, at the server's authoritative position.
   *
   * Sent even though the client could compute the time and place itself, because a bounce
   * that drifted by a centimetre would otherwise draw the blast somewhere the damage did not
   * happen. Predict the presentation; defer to the server for the consequence.
   */
  PROJECTILE_BURST: 9,
  /**
   * S→C broadcast: someone died, and who did it.
   *
   * The single source of kill attribution. The shot event's `killed` bit stays for the
   * shooter's own immediate feedback, but it cannot be the feed's source: a grenade kill
   * produces no shot event at all, so a feed built on shots would silently omit every
   * explosion — and the victim would get a death cam with nobody in it.
   */
  KILL: 10,
  /**
   * S→C, to one client only: which way you are now facing.
   *
   * The client owns its own look — it samples the mouse, sends yaw and pitch in every input,
   * and the server takes them as given. That is correct during play and wrong at exactly one
   * moment: the instant you (re)spawn somewhere you have never been. The map author picked a
   * facing for that spawn, and until this message existed nobody read it. `spawn.yaw` was in
   * the schema, validated by `map:check`, and dead on both tiers — every player on every map
   * spawned looking down -Z, which on `blackline-yard` is a wall 2.5m away.
   *
   * Per-recipient like WEAPON, and for the same reason: it is a statement about one player
   * that no other client can act on. 2 bytes, at most a few times a minute.
   */
  SPAWN: 11,
  /**
   * S→C broadcast: which cosmetic a player is wearing.
   *
   * Not a snapshot field, deliberately. A skin never changes during a life and rarely during
   * a match, so paying for it 20 times a second per player would be paying continuously for a
   * value that is set once. It is sent when someone joins (to everyone) and as a roster of
   * everyone already present (to the joiner) — two bytes per player, twice per match.
   *
   * Cosmetics are the meta tier's business: the skin arrives inside the signed join ticket,
   * never from the client, for the same reason the loadout does not. A client that stated its
   * own cosmetic could state a rank it has not earned.
   */
  PLAYER_INFO: 12,
  /**
   * S→C broadcast: a weapon is lying on the ground.
   *
   * Spawned when a player dies (their primary drops where they fell) or swaps (their old
   * primary drops where they stood). Event-driven like the grenade throw — a pickup's whole
   * life costs one spawn and one gone message, never a per-tick position, because a ground
   * weapon does not move. A joiner gets the live set as a roster, one message per pickup,
   * the same pattern PLAYER_INFO uses.
   */
  PICKUP_SPAWN: 13,
  /**
   * S→C broadcast: a ground weapon is gone — taken by someone or expired.
   *
   * The reason travels as one bit because the client renders the two differently (a take can
   * flash/chime, an expiry just removes) and cannot infer it: from the outside, "someone
   * grabbed it the tick it expired" and "it timed out" look identical.
   */
  PICKUP_GONE: 14,
  /**
   * S→C: something happened to the bomb — taken, dropped, planted, defused, detonated.
   *
   * Event-driven and 7 bytes, like the pickups and the grenade above it, and for the same
   * reason: the cause is sent, never the effect. A plant in progress is not on the wire at
   * all — the client holding the button already knows it is holding the button, and draws
   * its own bar. See `bomb-codec.ts`.
   */
  BOMB: 15,
  /**
   * S→C broadcast: a player left a decal on a surface.
   *
   * Cause, not effect — the same shape as the grenade's throw and the pickup's spawn. The
   * client sends a button bit and nothing else; the server raycasts against its own collision
   * world and broadcasts where it landed, because a client naming its own impact point could
   * spray through a wall. Rate limited server-side (`SPRAY_COOLDOWN_MS`), and the server keeps
   * no record afterwards: a decal has no gameplay consequence, so a late joiner is told
   * nothing and simply sees what is sprayed from then on. See `spray-codec.ts`.
   */
  SPRAY: 16,
  /**
   * S→C broadcast: a player used an emote — in this game a radio callout, two bytes.
   *
   * The SKU rides the event rather than the join payload, so nothing about an emote is join
   * state and the roster does not grow by a byte for a category that is silent most of a
   * match. Gated server-side while a competitive round is live (`EMOTE_LIVE_ROUND_POLICY`),
   * which is the whole reason the trigger is a server decision and not a client one. See
   * `emote-codec.ts`.
   */
  EMOTE: 17,
} as const;

// ─── Shots ──────────────────────────────────────────────────────────

/**
 * Shot event layout: 20 of the 24 bits in a 3-byte message, with 4 to spare.
 *
 * The zone field was added in P2 and cost nothing — the message was already padded to a byte
 * boundary. An earlier note in `weapons.md` claimed hit zones "need a hit location the 3-byte
 * shot event has no room for"; it was counting the payload, not the padding, and was wrong.
 */
export const SHOT_BITS = {
  shooterId: 4,
  fireSeq: 8,
  /** 0 = the shot hit nothing (air or geometry); 1 = it hit `targetId`. */
  hit: 1,
  targetId: 4,
  killed: 1,
  /**
   * Which band of the target the shot landed in — 0 head, 1 torso, 2 limb, matching
   * `HIT_ZONE`. Meaningless when `hit` is 0.
   *
   * On the wire rather than inferred client-side, because the client cannot infer it: the
   * server resolves against a *rewound* position, and the client's rendered position for that
   * target is a different moment in time. A client guessing the zone from its own view would
   * disagree with the damage it then sees applied.
   */
  zone: 2,
  /**
   * The spread roll — one signed offset each for yaw and pitch, spanning ±`SHOT_ROLL_MAX_RAD`
   * (shot-codec.ts) — present only on the copy sent to the shooter, which is 5 bytes instead
   * of the broadcast's 3. Ten bits is a 0.09° step over 90°: a 3cm error at 20m, on a tracer.
   */
  deviationYaw: 10,
  deviationPitch: 10,
} as const;

export interface ShotEvent {
  shooterId: number;
  fireSeq: number;
  /** Entity hit, or null for a miss. */
  targetId: number | null;
  /** True when this shot took the target's last health. */
  killed: boolean;
  /**
   * Where on the target it landed — see `HIT_ZONE` in `hitscan.ts`. Null on a miss.
   *
   * Typed as a plain number here rather than importing `HitZone`, because `protocol.ts` is
   * the bottom of the dependency graph and importing from `hitscan.ts` would invert it.
   */
  zone: number | null;
  /**
   * Where inside the cone the server rolled this shot: yaw and pitch offsets from the aim, in
   * radians. Zero for an exact shot; for a shotgun, the pellet the event reports.
   *
   * Only on the shooter's own copy of the event. The client cannot know it — the roll is the
   * one part of a shot the server keeps to itself — and without it the shooter's tracer went
   * straight down the crosshair while the bullet did not: every hip shot *looked* like a hit,
   * the hitmarker never came, and the only reading left was "the crosshair lies". Not on the
   * broadcast, because two more bytes on every shot to every client put the 16-player worst
   * case over its ceiling (`yarn budget`); remote tracers were never anyone's hit indicator.
   */
  deviation?: { yaw: number; pitch: number };
}

export type MessageType = (typeof MSG)[keyof typeof MSG];

// ─── Simulation timing ──────────────────────────────────────────────

/** Fixed simulation rate. Every input is integrated at exactly this step. */
export const TICK_RATE = 60;
export const TICK_MS = 1000 / TICK_RATE;

/** Snapshot broadcast rate. Clients interpolate between snapshots. */
export const SNAPSHOT_RATE = 20;
/** Simulation ticks between snapshots. Must divide evenly into TICK_RATE. */
export const TICKS_PER_SNAPSHOT = TICK_RATE / SNAPSHOT_RATE;

/** Remote-entity interpolation delay. Two snapshot intervals of slack absorbs
 * one dropped packet without visible stutter. */
export const INTERP_DELAY_MS = (1000 / SNAPSHOT_RATE) * 2;

/**
 * Longest window the server will rewind for lag-compensated hit resolution.
 *
 * 300ms, and the number is derived rather than picked. A shot is resolved against the
 * server time the shooter was *rendering*, which is behind the server's present by:
 *
 *   round trip (the input's `ackTick` is that old by the time it arrives)
 * + INTERP_DELAY_MS (100ms — remote players are played back that far in the past)
 * + up to one snapshot interval (50ms — `ackTick` is quantized to the 20Hz stream)
 *
 * At the 150ms RTT this game is designed around that is 250–300ms. A 200ms cap — the
 * figure in the first draft of ADR-0001, taken from one-way latency — would have
 * under-compensated every typical-latency player, forcing them to lead targets while
 * a LAN player did not. Measured by `rewindMsFor`, not assumed.
 *
 * The cap is a fairness knob, and raising it is not free: the further back the server
 * honours, the more often a target dies after reaching cover on their own screen.
 * Beyond ~150ms RTT compensation now saturates deliberately, so the disadvantage of a
 * bad connection lands on the player who has it.
 */
export const MAX_REWIND_MS = 300;

export const MAX_PLAYERS = 16;
/** Width of the present-entity bitmask: one bit per slot. */
export const MAX_PLAYERS_BITS = MAX_PLAYERS;

// ─── World bounds ───────────────────────────────────────────────────

/**
 * Quantization bounds for positions. Anything outside is clamped, so these must
 * comfortably contain every playable map plus death-fall space below it.
 *
 * Horizontal: 256m over 16 bits → 3.9mm steps (±2.0mm error).
 * Vertical:    64m over 12 bits → 15.6mm steps (±7.8mm error).
 * Both are inside the sub-1cm requirement, and the narrower vertical field pays
 * for itself in the per-entity budget.
 */
export const WORLD = {
  minXZ: -128,
  maxXZ: 128,
  minY: -8,
  maxY: 56,
} as const;

// ─── Field widths ───────────────────────────────────────────────────

export const BITS = {
  /** Entity id — indexes into the room's player slots, so MAX_PLAYERS bounds it. */
  entityId: 4,
  fieldMask: 5,
  posXZ: 16,
  posY: 12,
  /** Body yaw for remote players: 0.70° steps, indistinguishable at a distance. */
  yaw: 9,
  /** Remote pitch only drives head/weapon pose, so 0.71° steps are plenty. Unlike
   * yaw this is a plain value over [-π/2, π/2], not an angle over a full turn. */
  pitch: 8,
  /** 0..100 fits in 7 bits. */
  health: 7,
  state: 4,
  /**
   * Weapon id, on both the entity snapshot's status field and the kill-feed event
   * (`KILL_BITS.weapon`). Widened 3 -> 4 bits in Sprint 5 Batch 5 to fit a ninth weapon (0-15
   * instead of 0-7): `WEAPONS` used the full 0-7 range with nothing spare, so a battle rifle
   * could not be added without this. Widened again 4 -> 5 bits on 2026-09-08 (0-31) for the
   * third roster — ids 15-20 — by the same argument, with the same measured cost: +1 bit per
   * entity on the full snapshot only, the kill event still inside its 2-byte frame (15 of 16
   * bits), the pickup spawn now exactly 64 of its 64 bits, and the projectile throw 94 of 96.
   *
   * Priced, not assumed: this field is not per-tick state — it rides on the same change-bit
   * as `state` (`FIELD.status`) and is only sent when a weapon switch or a kill happens, never
   * on the typical position/yaw delta the steady-state downstream budget is measured against.
   * `yarn --cwd realtime budget` and `snapshot-codec.test.ts`'s gated 8/16-player assertions
   * (which encode only `FIELD.position | FIELD.yaw`) are therefore unchanged bit-for-bit by
   * this widening. The only things that grow are the full snapshot sent on join/baseline-loss
   * (+1 bit/entity, rounding to +1 B at 8 players and +2 B at 16 — see `FULL_ENTITY_BITS`) and
   * the kill event, which absorbed the extra bit inside `KILL_BYTES`'s existing 2-byte frame
   * for free (13 bits used of 16 before, 14 after).
   */
  weapon: 5,
  serverTick: 32,
  baselineTick: 32,
  inputSeq: 16,
  entityCount: 5,
  /** One bit per player slot: which entities exist this tick. */
  presentMask: MAX_PLAYERS_BITS,
} as const;

/** Local aim precision, sent upstream at full width — this is the player's own
 * aim, where quantization error is felt directly. 16 bits = 0.0055° steps. */
export const INPUT_BITS = {
  yaw: 16,
  pitch: 16,
} as const;

// ─── Input packet ───────────────────────────────────────────────────

/**
 * Fixed 12-byte layout, little-endian, byte-aligned via DataView.
 *
 * Deliberately NOT bit-packed, unlike snapshots. Bit-packing would save one byte
 * (84 bits → 11 bytes) but upstream at 60Hz is only ~0.72 KB/s, comfortably
 * inside the 1.2 KB/s budget. The byte-aligned form is far easier to inspect in
 * a packet dump, and inspectability is worth more than 60 bytes/s.
 *
 *   0  u16  seq          input sequence number, wraps
 *   2  u16  ackTick      low 16 bits of the server tick the client is RENDERING
 *   4  u16  buttons      BUTTON bitmask
 *   6  u16  yaw          quantized over [0, TAU)
 *   8  u16  pitch        quantized over [-π/2, π/2]
 *  10  u8   dtMs         frame delta, clamped — the server re-clamps regardless
 *  11  u8   fireSeq      shot id, 0 = no shot this frame
 *
 * `ackTick` is the **render clock**, not the newest snapshot received, and the difference is
 * load-bearing in both directions. It said "the last snapshot tick the client applied" for a
 * while, and both readings of that were acted on and both were wrong:
 *   - Implementing it that way makes it a step function advancing at 20Hz while the
 *     interpolator's playback clock advances every frame, so the server rewinds up to a full
 *     snapshot interval further than the client actually looked — 0–50ms, ~24cm of aim bias
 *     at sprint speed, invisible in aggregate hit counts. See `InputSampler.acknowledge`.
 *   - Validating it that way — "it must be a multiple of `TICKS_PER_SNAPSHOT`" — rejects
 *     every honest client. See `realtime/src/sim/ack-gate.ts`, which is where the one
 *     constraint that *is* safe to impose on this field lives.
 */
export const INPUT_PACKET_BYTES = 12;

export const INPUT_OFFSET = {
  seq: 0,
  ackTick: 2,
  buttons: 4,
  yaw: 6,
  pitch: 8,
  dtMs: 10,
  fireSeq: 11,
} as const;

/** Clamp bounds for the client-reported frame delta. A client claiming a long
 * frame is claiming extra movement, so this is a cheat surface: the server
 * clamps and additionally caps total movement per tick. */
export const DT_MS_MIN = 1;
export const DT_MS_MAX = 50;

export const PITCH_MIN = -Math.PI / 2;
export const PITCH_MAX = Math.PI / 2;

export const BUTTON = {
  forward: 1 << 0,
  back: 1 << 1,
  left: 1 << 2,
  right: 1 << 3,
  jump: 1 << 4,
  crouch: 1 << 5,
  sprint: 1 << 6,
  fire: 1 << 7,
  ads: 1 << 8,
  reload: 1 << 9,
  /**
   * Absolute weapon selection, not "next"/"previous", and the distinction is a netcode
   * decision rather than a UI one.
   *
   * A relative "cycle weapon" intent is an *event*: lose the packet carrying it and the
   * client and server disagree about what is being held until something else corrects them;
   * duplicate it and they disagree the other way. An absolute selection is *state* — the
   * same bit held for ten ticks selects the same weapon ten times, so a lost or repeated
   * input costs nothing. Inputs are already resent (`sequence.ts`), which makes a
   * self-correcting encoding worth three bits.
   */
  selectPrimary: 1 << 10,
  selectSidearm: 1 << 11,
  selectUtility: 1 << 12,
  /**
   * Pick up the nearest ground weapon. State, not an event, like the slot selections above
   * and for the same reason: held interact is re-evaluated by the server every tick, so a
   * lost or duplicated packet costs nothing. The server gates it (range, alive, phase, a
   * swap cooldown) — the client never predicts a pickup succeeding, exactly as it never
   * predicts where inside the spread cone a shot lands.
   */
  interact: 1 << 13,
  /**
   * Leave a spray, and use an emote — the last two bits of the `buttons` u16.
   *
   * Events in the player's head, state on the wire: both are re-read every tick like every
   * other button, and the server's own cooldown is what turns a held key into one spray or
   * one callout. That is deliberate, and it is the same rule `interact` follows — an edge
   * detected client-side would mean a lost packet costs the player the thing they bought,
   * while a held bit costs nothing to resend.
   *
   * Neither is ever predicted: the client does not draw its own decal or play its own line
   * until the server says it happened, because the server is the side that knows whether the
   * cooldown has run and whether a competitive round is live.
   */
  spray: 1 << 14,
  emote: 1 << 15,
} as const;

/** Which loadout slot a button mask is asking for, or null if it is not asking. */
export const WEAPON_SLOT = {
  primary: 0,
  sidearm: 1,
  utility: 2,
} as const;

export type WeaponSlot = (typeof WEAPON_SLOT)[keyof typeof WEAPON_SLOT];

/**
 * Slot requested by this input, or null.
 *
 * Lowest slot wins when several bits are set at once. That only happens from a malformed or
 * hostile client, and picking deterministically is what matters — client and server must
 * reach the same answer from the same bytes, whatever the bytes are.
 */
export function requestedSlot(buttons: number): WeaponSlot | null {
  if (buttons & BUTTON.selectPrimary) return WEAPON_SLOT.primary;
  if (buttons & BUTTON.selectSidearm) return WEAPON_SLOT.sidearm;
  if (buttons & BUTTON.selectUtility) return WEAPON_SLOT.utility;
  return null;
}

export interface InputPacket {
  seq: number;
  ackTick: number;
  buttons: number;
  yaw: number;
  pitch: number;
  dtMs: number;
  fireSeq: number;
}

// ─── Snapshots ──────────────────────────────────────────────────────

/** Which fields an entity update carries. */
export const FIELD = {
  /** x, y and z together — position components always change as a group. */
  position: 1 << 0,
  yaw: 1 << 1,
  pitch: 1 << 2,
  health: 1 << 3,
  /** state flags and weapon id together — both are rare, so one bit covers them. */
  status: 1 << 4,
} as const;

export const FULL_FIELD_MASK = FIELD.position | FIELD.yaw | FIELD.pitch | FIELD.health | FIELD.status;

export const ENTITY_STATE = {
  alive: 1 << 0,
  crouching: 1 << 1,
  sprinting: 1 << 2,
  airborne: 1 << 3,
} as const;

/** Full authoritative state for one entity, as the simulation holds it. */
export interface EntityState {
  id: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  health: number;
  state: number;
  weapon: number;
}

/** A partial entity update as it appears on the wire. Only fields named by
 * `mask` are present; everything else the client carries forward. */
export interface EntityUpdate {
  id: number;
  mask: number;
  x?: number;
  y?: number;
  z?: number;
  yaw?: number;
  pitch?: number;
  health?: number;
  state?: number;
  weapon?: number;
}

/** Loop diagnostics the server publishes for the debug overlay (MSG.SERVER_STATS). */
export interface ServerStatsMessage {
  ticks: number;
  measuredHz: number;
  droppedTicks: number;
  /** Simulation step cost in milliseconds. */
  stepP50: number;
  stepP99: number;
  stepMax: number;
  players: number;
  inputStarvedTicks: number;
  /**
   * Inputs discarded because a player's queue was full.
   *
   * On the wire because it is the single number P0's gap 3 turns on: overflow breaks the
   * one-step-per-input invariant prediction depends on, and it took the measured correction
   * rate from 0.28% to 1.30%. A diagnostic nobody can see is a diagnostic nobody checks.
   */
  inputsDroppedOverflow: number;
  /** Mean downstream snapshot bytes per second, per client. */
  snapshotBytesPerSecond: number;
}

export interface Snapshot {
  /** Absolute simulation tick this snapshot describes. */
  serverTick: number;
  /**
   * Bit i set means entity i exists in the match right now.
   *
   * Needed because a delta only carries entities that *changed*: absence from the
   * entity list cannot distinguish "unchanged" from "left the match". Without this
   * the client can never despawn anyone, and dead capsules accumulate on screen.
   * Costs 2 bytes per snapshot (40 B/s), which is cheaper than any per-entity
   * alternative.
   */
  presentMask: number;
  /** Tick this delta is relative to; 0 means a full, self-contained snapshot. */
  baselineTick: number;
  /** Highest input seq the server has consumed — drives client reconciliation. */
  ackedInputSeq: number;
  entities: EntityUpdate[];
}

/** Bits a full (unmasked) entity occupies. Used by the budget tests. */
export const FULL_ENTITY_BITS =
  BITS.entityId +
  BITS.fieldMask +
  BITS.posXZ * 2 +
  BITS.posY +
  BITS.yaw +
  BITS.pitch +
  BITS.health +
  BITS.state +
  BITS.weapon;

export const SNAPSHOT_HEADER_BITS =
  BITS.serverTick + BITS.baselineTick + BITS.inputSeq + BITS.entityCount + BITS.presentMask;
